import { resolveBehaviorFlowStateKey } from "./flowState.js";
import {
  runRenderBehaviorFlows,
  runRuntimeBehaviorEffects,
} from "./runBehaviorFlows.js";
import type {
  CompiledBehavior,
  RuntimeContext,
  RuntimeEffect,
  RuntimeFlowContinuation,
  RuntimePatch,
} from "./types.js";

/** Merges layout node geometry into the runtime context for animation resolution. */
export function withCurrentNodeGeometry(
  ctx: RuntimeContext,
  geometry: {
    x: number;
    y: number;
    width: number;
    height: number;
    rotation?: number;
    skewX?: number;
    skewY?: number;
  },
): RuntimeContext {
  return {
    ...ctx,
    __currentNodeContext: {
      rotation: geometry.rotation ?? 0,
      skewX: geometry.skewX ?? 0,
      skewY: geometry.skewY ?? 0,
      x: geometry.x,
      y: geometry.y,
      width: geometry.width,
      height: geometry.height,
    },
  };
}

export const RUNTIME_DOM_TARGET_KINDS = ["animation"] as const;

export type NodeBehaviorRunResult = {
  patches: RuntimePatch[];
  effects: RuntimeEffect[];
  continuations: RuntimeFlowContinuation[];
  runtimeDomTarget?: boolean;
};

function runtimePatchKey(patch: RuntimePatch): string {
  const target = patch.barLoaderTarget
    ? `${patch.barLoaderTarget.weight}:${patch.barLoaderTarget.part}`
    : "root";
  return `${patch.nodeId}:${target}:${patch.key}`;
}

function mergeRuntimePatches(
  current: RuntimePatch[],
  incoming: RuntimePatch[],
): RuntimePatch[] {
  if (incoming.length === 0) {
    return current;
  }

  const byKey = new Map(
    current.map((patch) => [runtimePatchKey(patch), patch] as const),
  );

  for (const patch of incoming) {
    byKey.set(runtimePatchKey(patch), patch);
  }

  return Array.from(byKey.values());
}

function persistRuntimePatches(
  ctx: RuntimeContext,
  ownerNodeId: string,
  patches: RuntimePatch[],
): RuntimePatch[] {
  let patchState = ctx.__runtimePatchStateByNodeKey;
  if (!patchState) {
    patchState = new Map();
    ctx.__runtimePatchStateByNodeKey = patchState;
  }

  const flowStateKey = resolveBehaviorFlowStateKey(ownerNodeId, ctx);
  const current = patchState.get(flowStateKey) ?? [];
  const next = mergeRuntimePatches(current, patches);

  if (next.length > 0) {
    patchState.set(flowStateKey, next);
  }

  return next;
}

function readPersistedRuntimePatches(
  ctx: RuntimeContext,
  ownerNodeId: string,
): RuntimePatch[] {
  const flowStateKey = resolveBehaviorFlowStateKey(ownerNodeId, ctx);
  return ctx.__runtimePatchStateByNodeKey?.get(flowStateKey) ?? [];
}

function readPersistedRuntimeContinuations(
  ctx: RuntimeContext,
  ownerNodeId: string,
): RuntimeFlowContinuation[] {
  const flowStateKey = resolveBehaviorFlowStateKey(ownerNodeId, ctx);
  return ctx.__runtimeContinuationStateByNodeKey?.get(flowStateKey) ?? [];
}

function dedupeContinuations(
  continuations: RuntimeFlowContinuation[],
): RuntimeFlowContinuation[] {
  return Array.from(
    new Map(
      continuations.map(
        (continuation) => [continuation.key, continuation] as const,
      ),
    ).values(),
  );
}

function mergeContinuations(
  current: RuntimeFlowContinuation[],
  incoming: RuntimeFlowContinuation[],
): RuntimeFlowContinuation[] {
  if (current.length === 0) {
    return dedupeContinuations(incoming);
  }

  if (incoming.length === 0) {
    return current;
  }

  const byKey = new Map(
    current.map((continuation) => [continuation.key, continuation] as const),
  );

  for (const continuation of incoming) {
    byKey.set(continuation.key, continuation);
  }

  return Array.from(byKey.values());
}

function persistRuntimeContinuations(
  ctx: RuntimeContext,
  ownerNodeId: string,
  continuations: RuntimeFlowContinuation[],
): RuntimeFlowContinuation[] {
  let continuationState = ctx.__runtimeContinuationStateByNodeKey;
  if (!continuationState) {
    continuationState = new Map();
    ctx.__runtimeContinuationStateByNodeKey = continuationState;
  }

  const flowStateKey = resolveBehaviorFlowStateKey(ownerNodeId, ctx);
  const consumedKeys = new Set(
    (ctx.__resumeContinuations ?? []).map((continuation) => continuation.key),
  );
  const current = (continuationState.get(flowStateKey) ?? []).filter(
    (continuation) => !consumedKeys.has(continuation.key),
  );
  const next = mergeContinuations(current, continuations);

  if (next.length > 0) {
    continuationState.set(flowStateKey, next);
  } else {
    continuationState.delete(flowStateKey);
  }

  return next;
}

/**
 * Runs declarative patches and runtime effects for a layout node's compiled behavior.
 */
export function runCompiledNodeBehavior(
  behavior: CompiledBehavior | undefined,
  ctx: RuntimeContext,
  ownerNodeId: string,
): NodeBehaviorRunResult {
  if (!behavior) {
    return { patches: [], effects: [], continuations: [] };
  }

  const targetedBehaviorNodeIds = ctx.__targetedBehaviorNodeIds;
  if (targetedBehaviorNodeIds && !targetedBehaviorNodeIds.has(ownerNodeId)) {
    return {
      patches: readPersistedRuntimePatches(ctx, ownerNodeId),
      effects: [],
      continuations: readPersistedRuntimeContinuations(ctx, ownerNodeId),
      runtimeDomTarget: behavior.requiresRuntimeDomTarget === true,
    };
  }

  const renderResult = runRenderBehaviorFlows(behavior, ctx, ownerNodeId);
  const runtimeResult =
    ctx.__suppressRuntimeEffects === true
      ? { patches: [], effects: [], continuations: [] }
      : runRuntimeBehaviorEffects(behavior, ctx, ownerNodeId);

  const continuations = dedupeContinuations([
    ...renderResult.continuations,
    ...runtimeResult.continuations,
  ]);

  return {
    patches: persistRuntimePatches(ctx, ownerNodeId, renderResult.patches),
    effects: [...renderResult.effects, ...runtimeResult.effects],
    continuations: persistRuntimeContinuations(ctx, ownerNodeId, continuations),
    runtimeDomTarget: behavior.requiresRuntimeDomTarget === true,
  };
}

/** Exposes DOM requirements and element interactions without coupling them to a visual kind. */
export function applyRuntimeDomTargetFlags<
  T extends {
    runtimeDomTarget?: boolean;
    runtimeDomTargetKinds?: Array<"animation" | "event">;
    style: Record<string, unknown>;
    key?: string;
  },
>(node: T, runtimeDomTarget?: boolean, behavior?: CompiledBehavior): T {
  const clickable = behavior?.eventHandlers.some(
    (handler) => handler.eventKind === "onClick",
  );
  const disabled = node.style.customActionDisabled;
  if (!runtimeDomTarget && !clickable && disabled == null) {
    return node;
  }

  const { customActionDisabled: _disabled, ...style } = node.style;
  return {
    ...node,
    ...(runtimeDomTarget
      ? {
          runtimeDomTarget: true,
          runtimeDomTargetKinds: [...RUNTIME_DOM_TARGET_KINDS],
        }
      : {}),
    ...(clickable ? { clickable: true, clickTargetKey: node.key } : {}),
    ...(disabled != null
      ? { interactionDisabled: Boolean(disabled), style }
      : {}),
  };
}
